import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { MessageService } from 'primeng/api';
import {
    Observable,
    of,
    combineLatest,
    filter,
    finalize,
    catchError,
    map,
    mergeMap,
    tap,
    shareReplay,
    take,
    BehaviorSubject,
    switchMap,
    forkJoin,
    Subject
} from 'rxjs';

import { ReleaseStatus } from '../../../api/wire/enums/release-status.enum';
import { WireRegion } from '../../../api/wire/enums/wire-region.enum';
import { NewswireApiModel } from '../../../api/wire/models/newswire-api.model';
import { WireQuotaApiModel } from '../../../api/wire/models/wire-quota-api.model';
import { WireApi, WirePatchModel } from '../../../api/wire/wire.api';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { DeliverySchedule } from '../../../Common/Forms/components/DeliveryScheduleInput/models/delivery-schedule';
import { LeftSidebarListItem } from '../../../Common/Layout/models/left-sidebar-list-item.model';
import { attributeTypesEnum } from '../../../Common/Misc/enums/attribute-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { NotesChange } from '../../../Common/TargetingShared/components/UserNotes/user-notes.component';
import { validateEmail } from '../../../Common/Misc/helpers/formatting/email.helper';
import { AttributeModel } from '../../../Common/Misc/models/attribute.model';
import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { ImageDisplayElementModel } from '../../../Common/PageBuilder/models/image-display-element.model';
import { RichTextDisplayElementModel } from '../../../Common/PageBuilder/models/rich-text-display-element.model';
import { TextStyleModel } from '../../../Common/PageBuilder/models/text-style.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { AmplifyRouteTypeEnum } from '../../AmplifyRouter/enums/amplify-route-type.enum';
import { AmplifyRoutingService } from '../../Core/services/amplify-routing.service';
import { WireBlockTypeEnum } from '../enums/wire-block-type.enum';
import { WireErrors } from '../enums/wire-errors';
import { WireValidationLevel } from '../enums/wire-validation-level.enum';
import { ContactDisplayElementModel } from '../models/contact-display-element.model';
import { SocialLinkDisplayElementModel } from '../models/social-link-display-element.model';
import { wireContactBlockType, wireTextBlockType } from '../models/wire-block-types.model';
import { WireCompanyBioModel } from '../models/wire-company-bio.model';
import { WireListItemModel } from '../models/wire-list-item.model';
import { WireModel } from '../models/wire.model';
import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { CurrentResourceAbstractService } from '../../../Common/Basic/service/current-resource-abstract.service';
import { ResourceActionTypes } from '../../../Common/Basic/enums/resource-actions.enum';
import { ResourceAction } from '../../../Common/Basic/service/resource-abstract.service';
import { LocalizationReplacementsModel } from '../../../Core/Localization/models/localization-replacements.model';
import { NewswireReleaseSummaryModel } from '../../../api/wire/models/newswire-release-summary.model';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { TextInputDialogModel } from '../../../Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';

export enum WireComponentTabEnum {
    details,
    revisions,
    blockForm
}

@Injectable()
export class WireService extends CurrentResourceAbstractService<WireModel, NewswireApiModel, WireListItemModel, NewswireReleaseSummaryModel> implements OnDestroy {

    // NewsDirect has a requirement that sum size of all images should not exceed 20mb
    readonly MAX_TOTAL_IMAGE_SIZE: number = 20 * 1024 * 1024;

    private currentRoute$ = this.amplifyRoutingService.getCurrentRoute();
    private updateVisibilitySubject = new Subject<{id: number; isHidden: boolean}>();
    private refreshEditorialWireList = new BehaviorSubject<void>(undefined);

    editorialWireList$ = combineLatest([ this.refreshEditorialWireList, this.currentUserStateService.getPermissions()]).pipe(
        switchMap(([refresh, permissions]) => {
            if (permissions?.indexOf(userPermissionsEnum.wireEditorial) !== -1) {

                return this.wireApi.getReadyToReviewWireList();
            }

            return of([]);
        }),
        map(list => list.map(apiWireListItem => new WireListItemModel(apiWireListItem)))
    );

    private updateVisibility$ = this.updateVisibilitySubject.asObservable().pipe(
        switchMap(({id, isHidden}) => {
            const wirePatch: WirePatchModel = {
                id: id,
                hidden: isHidden
            };
            return this.wireApi.patch(wirePatch, this.isEditor).pipe(
                tap(() => this.refreshWireList()),
                catchError((error: HttpErrorResponse) => {
                    this.actionCompletedSubject.next({
                        actionType: ResourceActionTypes.UPDATE,
                        error
                    });
                    return of(null);
                }),
            );
        })
    );

    private isWireNameDuplicated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

    private isEditor = false;

    quotas$ = this.currentUserStateService.getPermissions().pipe(
        switchMap((permissions) => {
            if (permissions.includes(userPermissionsEnum.newswire) || permissions.includes(userPermissionsEnum.newswireplus)) {
                return this.wireApi.getMyQuotas().pipe(shareReplay(1));
            }

            return of([]);
        }),
    );
    private quotaWarningSubject = new BehaviorSubject<boolean>(false);
    quotaWarning$ = this.quotaWarningSubject.asObservable();
    quotaWarningCalculation$ = combineLatest([this.currentResource$, this.quotas$, this.currentResourceModified$])
        .pipe(tap(([wire, quotas]) => {
            if (!wire || wire.regions?.length === 0 || !quotas) {
                return false;
            }

            const insufficientQuota = wire.regions.some(region => !this.isQuotaSufficient(region, quotas));
            this.quotaWarningSubject.next(insufficientQuota);
        }));

    wireAttributes$ = combineLatest([this.currentResource$, this.currentUserStateService.getAccount()])
        .pipe(map(([wire, account]) => {
            return this.getWireAttributes(wire, account.name);
        }));

    wireRegionAttributes$ = combineLatest([this.currentResource$, this.quotas$])
        .pipe(map(([wire, quotas]) => {
            return this.getWireRegionAttributes(wire, quotas);
        }));

    isWireEditable$ = combineLatest([this.currentResource$, this.currentRoute$])
        .pipe(map(([currentWire, route]) => {
            if (!currentWire) {
                return false;
            }

            return (route.type === AmplifyRouteTypeEnum.wire && currentWire.status === ReleaseStatus.draft)
            || (route.type === AmplifyRouteTypeEnum.editorial && currentWire.status === ReleaseStatus.submitted);
        }));

    constructor(
        private wireApi: WireApi,
        private messageService: MessageService,
        private currentUserStateService: CurrentUserStateService,
        private amplifyRoutingService: AmplifyRoutingService,
        private websocketEvents: WebsocketAwareNeptuneService,
        private displayGroupsService: DisplayGroupsService
    ) {
        super();

        this.subscriptions.push(
            this.quotaWarningCalculation$.subscribe(),
            this.currentRoute$.pipe(
                switchMap((route => {
                    if (route) {
                        this.isEditor = route.type === AmplifyRouteTypeEnum.editorial;
                    }

                    if ((route.type === AmplifyRouteTypeEnum.wire || route.type === AmplifyRouteTypeEnum.editorial)) {
                        if ((route.value && !this.cachedResource) || (+route.value !== this.cachedResource?.id) ) {
                            if (route.value) {
                                this.loadingSubject.next(true);
                                if (this.isEditor) {
                                    return this.editorGetById(+route.value);
                                } else {
                                    return this.getById(+route.value);
                                }
                            }
                        }
                    }

                    return of(null);
                })),
                tap(wire => {
                    if (wire) {
                        this.currentResourceSubject.next(wire);
                        this.loadingSubject.next(false);
                    }
                })).subscribe(),

            this.websocketEvents.getWebsocketMessages().pipe(
                filter(x => !!x))
                .subscribe(
                    message => {
                        if (message.resourceType === ResourceType.newswire && message.slug.indexOf('newswire/releases') !== -1) {
                            this.refreshAllSubject.next();
                            this.refreshEditorialWireList.next();
                        }
                    }
                ),

            this.updateVisibility$.subscribe()
        );
    }

    getApiService(): MultiBaseAbstractApi<NewswireApiModel, NewswireReleaseSummaryModel> {
        return this.wireApi;
    };

    constructResourceModel(apiModel: NewswireApiModel): WireModel {
        return new WireModel(apiModel);
    }

    constructListModel(apiModel: NewswireReleaseSummaryModel): WireListItemModel {
        return new WireListItemModel(apiModel);
    }

    // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
    public refreshWireList() {
        this.refreshAllSubject.next();
        this.refreshEditorialWireList.next();
    }

    public copyWire(wire: WireListItemModel){
        let wireList;
        const listSubscription = this.all$.subscribe(list => wireList = list);
        const inputConfig: NewResourceDialogModel = {
            inputs: {
                heading: 'Amplify.copyRelease',
                initialValue: wire.name + this.localizationService.transform('Common.dialog.newResource.copy.suffix'),
                currentGroup: wire.groupId,
                okLabel: 'Common.dialog.newResource.copy',
                formValidator: (name: string) => {
                    if (wireList.find(w => w.name === name.trim())) {
                        return ['Amplify.wire.error.duplicateName'];
                    } else {
                        return [];
                    }
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    listSubscription.unsubscribe();
                    this.getById(wire.id).pipe(
                        mergeMap(_wire => {
                            _wire.name = name;
                            _wire.status = ReleaseStatus.draft;
                            _wire.groupId = group?.id;
                            _wire.isFavorite = false;
                            delete _wire.id;
                            return this.createResource(_wire, {actionType: ResourceActionTypes.COPY});
                        })
                    ).subscribe((newId: number) => {
                        if (newId) {
                            this.amplifyRoutingService.gotoWire(newId);
                        }
                        this.refreshWireList();
                    });

                    return of(null);
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, inputConfig);
    }

    /**
     * Helper method that deletes a wire from a LeftSidebarListItem
     */
    public deleteWire(listItem: LeftSidebarListItem) {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'amplify.deleteWireConfirmDialog',
                heading: this.localizationService.transform('Amplify.dialogs.deleteWire.heading'),
                message: this.localizationService.transform('Amplify.dialogs.deleteWire.message'),
                list: [listItem.label],
                okLabel: this.localizationService.transform('Tags.manageDialog.delete')
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.deleteResource(
                            listItem.value,
                            {
                                actionType: ResourceActionTypes.DELETE,
                                name: listItem.label
                            } as ResourceAction
                        ).subscribe({
                            next: () => {
                                if (this.cachedResource && this.cachedResource.id === listItem.value) {
                                    this.amplifyRoutingService.gotoWireHype();
                                }
                                this.refreshWireList();
                            }
                        });
                    } else {
                        this.refreshWireList();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    /**
     * Similar logic is used across different services.
     *
     * @TODO Consider consolidating repeated bulk deletion logic using shared CRUD interfaces.
     */
    confirmBulkDeleteWire(items: AuditableModel[], groups: DisplayGroupModel[]): Observable<boolean> {
        const list = items.map(item => item.name);
        const secondaryList = groups.map(item => item.name);

        return new Observable((subscriber) => {
            let isListSelected: boolean = true;
            let isSecondaryListSelected: boolean = false;

            const confirmConfig: ConfirmDialogModel = {
                inputs: {
                    id: 'amplify.deleteWireConfirmDialog',
                    heading: 'Amplify.dialogs.bulkDeleteWire.heading',
                    message: 'Common.confirmDialog.message',
                    list,
                    listLabel: 'Amplify.dialogs.bulkDeleteWire.checkboxLabel',
                    isListSelected,
                    secondaryList,
                    secondaryListLabel: 'Common.confirmDialog.checkboxLabel.groups',
                    isSecondaryListSelected,
                    okLabel: 'Core.button.delete'
                },
                outputs: {
                    listSelectionChanged: (isSelected: boolean) => {
                        isListSelected = isSelected;
                    },
                    secondaryListSelectionChanged: (isSelected: boolean) => {
                        isSecondaryListSelected = isSelected;
                    },
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            if (isListSelected) {
                                const ids = items.map(item => item.id);
                                this.bulkDeleteWire(ids);
                            }

                            if (isSecondaryListSelected) {
                                groups.forEach(group => this.displayGroupsService.deleteGroup(group));
                            }
                        }
                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
        });

    }

    private bulkDeleteWire(ids: number[]) {
        const observables = ids.map(id => {
            return this.wireApi.deleteById(id);
        });
        forkJoin(observables).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Amplify.dialogs.bulkDeleteWire.success.summary',
                    detail: this.localizationService.transform('Amplify.dialogs.bulkDeleteWire.success.detail', {number: result.length})
                });
                if (this.cachedResource && ids.includes(this.cachedResource.id)) {
                    this.amplifyRoutingService.gotoWireHype();
                }

                this.refreshAllSubject.next();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Amplify.dialogs.bulkDeleteWire.error.summary',
                    detail: 'Amplify.dialogs.bulkDeleteWire.error.detail'
                });

                this.refreshAllSubject.next();
            }
        });
    }

    createNewTextBlock(): BlockModel {
        const defaultTextBlock = new BlockModel(null, wireTextBlockType, new wireTextBlockType.displayElementType());
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).styles = new TextStyleModel();
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).styles.fontSize = '12pt';
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enablePersonalizations = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enableBackgroundColor = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enableFontColor = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enableHtmlEditor = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enableLineHeight = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enableFontFamily = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enableFontSize = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enableInsertImage = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enableStrikethrough = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enableNumberedList = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enableBlockQuote = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).allowImage = false;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).pastePlainText = true;
        (<RichTextDisplayElementModel>defaultTextBlock.displayElement).enableAIHelper = true;

        return defaultTextBlock;
    }

    createAndRouteToNewWire(wire: WireModel) {
        this.amplifyRoutingService.gotoWire();
        this.updateCurrentWire(wire);
    }

    createBlankWire(wireRegion: WireRegion) {
        const newWire = new WireModel();
        newWire.regions.push(wireRegion);

        const defaultTextBlock = this.createNewTextBlock();
        const defaultContactBlock = new BlockModel(null, wireContactBlockType, new wireContactBlockType.displayElementType());
        newWire.blocks.push(defaultTextBlock, defaultContactBlock);

        this.createAndRouteToNewWire(newWire);
    }

    updateCurrentWire(wire: WireModel) {
        this.setCurrentResource(wire);
    }

    cloneFromExistingWire(id: number, wireRegion: WireRegion) {
        combineLatest([
            this.getById(id),
            this.currentUserStateService.getUser().pipe(filter(x => !!x))]
        ).pipe(take(1)).subscribe(([newWire, currentUser]) => {
            delete newWire.id;
            delete newWire.modifiedByUserId;
            delete newWire.modifiedDate;
            delete newWire.publishedUserId;
            delete newWire.newsDirectPublicationDate;
            newWire.createdByUserId = currentUser.id;
            newWire.createdDate = new Date();
            newWire.userNotes = null;
            newWire.name += ' (copy)';
            newWire.status = ReleaseStatus.draft;
            newWire.regions = [wireRegion];
            newWire.deliverySchedule = new DeliverySchedule(null, null);

            this.createAndRouteToNewWire(newWire);
        });
    }

    publish(wire: WireModel, bodyHtml: string) {
        if (!bodyHtml) {
            this.actionCompletedSubject.next({
                actionType: ResourceActionTypes.PUBLISH,
                error: 'noContent'
            });
            return;
        }

        this.loadingSubject.next(true);

        this.updateWire(wire, {actionType: ResourceActionTypes.PUBLISH}, false).pipe(
            mergeMap(() => this.wireApi.publish(wire.id, bodyHtml)),
            tap(() => this.actionCompletedSubject.next({actionType: ResourceActionTypes.PUBLISH, resource: wire})),
            catchError(err => {
                this.actionCompletedSubject.next({actionType: ResourceActionTypes.PUBLISH, error: err});
                return of(null);
            }),
            finalize(() => this.loadingSubject.next(false))
        ).subscribe(() => {
            this.amplifyRoutingService.gotoEditorialHype();

            this.reGetEditorById(wire.id);
            // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
            this.refreshWireList();
        });
    }

    // since editorGetById is only tied to the route, I created another function just to set the current resource
    reGetEditorById(id) {
        this.loadingSubject.next(true);
        this.editorGetById(id).subscribe(wire => {
            this.currentResourceSubject.next(wire);
            this.loadingSubject.next(false);
        });
    }

    saveNotes(notesChange: NotesChange, wire: WireModel): Observable<number> {
        const notesUpdate = {
            id: this.cachedResource.id,
            userNotes: notesChange.notes.map(note => note.toApiModel())
        };

        return this.wireApi.patch(notesUpdate, this.isEditor).pipe(
            tap(() => {
                this.actionCompletedSubject.next({
                    actionType: notesChange.type,
                    resource: wire,
                    refreshResource: true
                });
            }),

            catchError((error: HttpErrorResponse) => {
                this.actionCompletedSubject.next({
                    actionType: notesChange.type,
                    error
                });
                return of(null);
            }),
        );
    }

    getWireAttributes(wire: WireModel, accountName: string): AttributeModel[] {
        if (!wire?.createdDate) {
            return [];
        }

        const createdByUserAttribute: AttributeModel = {
            id: 0,
            label: 'createdBy',
            value: wire.createdByUserId,
            type: attributeTypesEnum.user,
        };

        const createdOnDateAttribute: AttributeModel = {
            id: 1,
            label: 'createdDate',
            value: wire.createdDate,
            type: attributeTypesEnum.utcOffsetDate,
        };

        const lastModifiedByUserAttribute: AttributeModel = {
            id: 2,
            label: 'lastModifiedBy',
            value: wire.modifiedByUserId,
            type: attributeTypesEnum.user
        };

        const lastModifiedDateAttribute: AttributeModel = {
            id: 3,
            label: 'lastModifiedDate',
            value: wire.modifiedDate,
            type: attributeTypesEnum.utcOffsetDate,
        };

        const publishedByUserAttribute: AttributeModel = {
            id: 4,
            label: 'publishedBy',
            value: wire.publishedUserId,
            type: attributeTypesEnum.user
        };

        const scheduledDateAttribute: AttributeModel = {
            id: 5,
            label: 'scheduledDate',
            value:  wire.deliverySchedule?.scheduledDateUTC || null,
            type: attributeTypesEnum.utcOffsetDate
        };

        const publishedDateAttribute: AttributeModel = {
            id: 5,
            label: 'publishedDate',
            value: wire.newsDirectPublicationDate,
            type: attributeTypesEnum.utcOffsetDate
        };

        const accountAttribute: AttributeModel = {
            id: 6,
            label: 'Amplify.wire.account',
            value: accountName,
            type: attributeTypesEnum.simple,
        };

        return [
            createdByUserAttribute,
            createdOnDateAttribute,
            lastModifiedByUserAttribute,
            lastModifiedDateAttribute,
            scheduledDateAttribute,
            publishedDateAttribute,
            publishedByUserAttribute,
            accountAttribute
        ].filter(x => !!x.value);
    }

    getWireRegionAttributes(wire: WireModel, quotas: WireQuotaApiModel[]): AttributeModel[] {
        if (!wire || !quotas?.length) {
            return [];
        }

        const region = wire.regions[0];
        let quota;
        quotas.forEach(x => {
            if (x.region === region) {
                quota = x.baselineWires + x.additionalWires - x.usedWires;
            }
        });

        const regionAttribute: AttributeModel = {
            id: 0,
            label: 'Amplify.wire.publicationRegions.region',
            value: 'WireRegion.' + region,
            type: attributeTypesEnum.simple,
        };

        const remainingQuotaAttribute: AttributeModel = {
            id: 0,
            label: 'Amplify.wire.publicationRegions.remainingReleases',
            icon: 'fa-regular fa-circle-info',
            iconToolTip: 'Amplify.wire.publicationRegions.remainingReleases.tooltip',
            value: quota,
            type: attributeTypesEnum.number,
        };

        return [
            regionAttribute,
            remainingQuotaAttribute
        ];
    }

    isQuotaSufficient(region: WireRegion, quotas: WireQuotaApiModel[]): boolean {
        let isQuotaSufficient = false;
        quotas.forEach(wireQuota => {
            if (wireQuota.region === region) {
                isQuotaSufficient = wireQuota.baselineWires + wireQuota.additionalWires - wireQuota.usedWires > 0;
            }
        });

        return isQuotaSufficient;
    }

    getValidationErrors(wire: WireModel, validationLevel: WireValidationLevel): WireErrors {
        const errors = new WireErrors();

        switch (validationLevel) {
            case WireValidationLevel.HEADER:
                this.validateHeader(wire, errors);
                break;
            case WireValidationLevel.DRAFT:
                this.validateName(wire, errors);
                break;
            case WireValidationLevel.SUBMITTED:
                this.validateHeader(wire, errors);
                this.validateName(wire, errors);
                errors.hasNoContactName = !wire.contactName || wire.contactName.trim().length === 0;
                errors.hasNoContactEmail = !wire.contactEmail || wire.contactEmail.trim().length === 0;
                errors.wrongEmailFormat = !errors.hasNoContactEmail && !validateEmail(wire.contactEmail);
                errors.hasCompanyLogoError = !!wire.companyLogo && !wire.companyLogo.url;
                errors.hasCompanyAddressError = !!wire.companyAddress && wire.companyAddress.isFieldEmpty();
                errors.hasNoTextBlockError = wire.blocks.map(block => block.type.key).indexOf(WireBlockTypeEnum.text) === -1;
                errors.hasScheduleDateError = !wire.deliverySchedule.isValid();
                errors.hasImageTotalSizeError = this.hasImageTotalSizeError(wire);
                this.validateWireBlocks(wire.blocks, errors);
                break;
        }

        errors.errorCount = Object.values(errors).filter(Boolean).length;

        return errors;
    }

    validateHeader(wire: WireModel, errors: WireErrors) {
        errors.hasNoHeadline = !wire.headline || wire.headline.trim().length === 0;
        errors.headlineExceedsByteLengthLimit = (new TextEncoder().encode(wire.headline)).length > 200;
        errors.hasNoCompanyName = !wire.companyName || wire.companyName.trim().length === 0;
        errors.hasNoDateline = !wire.dateline || wire.dateline.trim().length === 0;
    }

    validateName(wire: WireModel, errors: WireErrors): void {
        if (this.isEditorialReview()) {
            // In editorial review mode, name change is disabled
            // check duplicate name is unnecessary as users have already submitted releases
            return;
        }

        this.currentResourceModified();
        this.isNameDuplicated$(wire.name, wire.id).subscribe(x => {
            errors.nameExists = x;
            this.isWireNameDuplicated$.next(x);
        });
    }

    /**
     * Check the current wire's blocks for errors
     *
     * @return boolean - true if no errors
     */
    public validateWireBlocks(blocks: BlockModel[], errors: WireErrors): boolean {
        let isValid: boolean = true;
        for (let i = 0; i < blocks.length; i++) {
            const block = blocks[i];
            switch (block.type.key) {
                case WireBlockTypeEnum.text:
                    isValid = this.checkErrorsForTextBlock(block, errors) && isValid;
                    break;
                case WireBlockTypeEnum.companyBio:
                    isValid = this.checkErrorsForCompanyBioBlock(block, errors) && isValid;
                    break;
                case WireBlockTypeEnum.contact:
                    isValid = this.checkErrorsForContactBlock(block, errors) && isValid;
                    break;
                case WireBlockTypeEnum.links:
                    isValid = this.checkErrorsForLinksBlock(block, errors);
                    break;
                case WireBlockTypeEnum.image:
                case WireBlockTypeEnum.video:
                    isValid = this.checkErrorsForImageAndVideoBlock(block, errors);
                    break;
            }
        }

        return isValid;
    }

    /**
     * Check for errors on blocks
     *
     * @param block the item to check for errors on
     *
     * @returns boolean - true if no errors
     */
    checkErrorsForImageAndVideoBlock(block: BlockModel, errors?: WireErrors): boolean {
        const url = (block.displayElement as ImageDisplayElementModel).image.url;

        if (!url || url.trim().length === 0) {
            const errorMsg =  block.type.key === WireBlockTypeEnum.image ? 'Amplify.wire.error.block.imageRequired' : 'Amplify.wire.error.block.videoRequired';
            this.addErrorMsg(errorMsg, block.errors);
            if (errors) {
                errors.hasNoImageOrVideoContentError = true;
            }
            return false;
        }

        if (block.type.key === WireBlockTypeEnum.image && !block.imageFileSize) {
            this.addErrorMsg('Amplify.wire.error.block.invalidImage', block.errors);
            if (errors) {
                errors.invalidImageContentError = true;
            }
            return false;
        }

        block.errors = [];

        return true;
    }

    hasImageTotalSizeError(wire: WireModel): boolean {
        let totalImageSize = 0;
        wire.blocks.forEach(block => {
            if (block.type.key === WireBlockTypeEnum.image) {
                totalImageSize += block.imageFileSize;
            }
        });

        return totalImageSize > this.MAX_TOTAL_IMAGE_SIZE;
    }

    /**
     * Check for errors on blocks
     *
     * @param block the item to check for errors on
     *
     * @returns boolean - true if no errors
     */
    checkErrorsForTextBlock(block: BlockModel, errors?: WireErrors): boolean {
        const displayElement = block.displayElement as RichTextDisplayElementModel;
        const text = displayElement.text;
        let errorMsg;

        if (!text || !text.trim()) {
            errorMsg = 'Amplify.wire.error.block.textContentRequired';
            this.addErrorMsg(errorMsg, block.errors);
            if (errors) {
                errors.hasNoTextContentError = true;
            }
            return false;
        } else if (this.hasImage(displayElement.text)) {
            errorMsg = 'Amplify.wire.error.block.invalidTextContent';
            this.addErrorMsg(errorMsg, block.errors);
            if (errors) {
                errors.hasImageInTextBlock = true;
            }
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Amplify.wire.error.block.invalidContent'),
                detail: this.localizationService.transform('Amplify.wire.error.block.hasImageInTextBlock')
            });
            return false;
        }

        block.errors = [];

        return true;
    }

    checkErrorsForCompanyBioBlock(block: BlockModel, errors?: WireErrors): boolean {
        const displayElement = block.displayElement as WireCompanyBioModel;

        if (!displayElement.companyBio || !displayElement.companyBio.trim()) {
            const errorMsg = 'Amplify.wire.error.hasCompanyBioError';
            this.addErrorMsg(errorMsg, block.errors);
            if (errors) {
                errors.hasCompanyBioError = true;
            }

            return false;
        }

        block.errors = [];

        return true;
    }

    checkErrorsForContactBlock(block: BlockModel, errors?: WireErrors): boolean {
        const contact = (block.displayElement as ContactDisplayElementModel).contact;
        block.errors = [];

        if (!contact.name || !(contact.name.trim().length > 0)) {
            const errorMsg = 'Amplify.wire.error.hasNoContactName';
            this.addErrorMsg(errorMsg, block.errors);
        }
        if (!contact.email || !(contact.email.trim().length > 0)) {
            const errorMsg = 'Amplify.wire.error.hasNoContactEmail';
            this.addErrorMsg(errorMsg, block.errors);
        } else if (!validateEmail(contact.email)) {
            const errorMsg = 'invalidEmailFormat';
            this.addErrorMsg(errorMsg, block.errors);
        }

        if (block.errors.length > 0) {
            if (errors) {
                errors.hasContactBlockContentError = true;
            }
            return false;
        }

        return true;
    }

    checkErrorsForLinksBlock(block: BlockModel, errors?: WireErrors): boolean {
        const links = (block.displayElement as SocialLinkDisplayElementModel).socialLinks;
        block.errors = [];

        if (links.some(link => !link.url)) {
            block.errors.push('Amplify.wire.error.block.urlRequired');
        }

        if (links.some(link => !link.isUrlValid())) {
            block.errors.push('Amplify.fields.links.error');
        }

        if (block.errors.length > 0 && errors) {
            errors.hasSocialLinkError = true;
            return false;
        }
        return true;
    }

    editorUpdate(
        resource: WireModel,
        action: ResourceAction = {actionType: ResourceActionTypes.SAVE},
        broadcast: boolean = true
    ): Observable<number> {
        action.resource =  resource;
        return this.wireApi.editorUpdate(resource.toApiModel()).pipe(
            tap(x => {
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err,
                });
                return of(null);
            })
        );
    };

    updateWire(
        resource: WireModel,
        action: ResourceAction = {actionType: ResourceActionTypes.SAVE},
        broadcast: boolean = true,
    ):  Observable<number>{
        if (this.isEditor) {
            return this.editorUpdate(resource, action, broadcast).pipe(tap(() => this.refreshEditorialWireList.next()));
        }

        return this.updateResource(resource, action, broadcast);
    }

    editorGetById(id: number): Observable<WireModel> {
        return this.wireApi.editorGetById(id).pipe(
            map(x => this.constructResourceModel(x))
        );
    };

    /**
     * Appends an error message if its not already there
     */
    private addErrorMsg(errorMsg: string, errors: string[]) {
        if (errors.indexOf(errorMsg) === -1) {
            errors.push(errorMsg);
        }
    }


    /**
     * check if the block contains any image
     */
    private hasImage(html: string): boolean {
        const blockContent: Document = new DOMParser().parseFromString(html, 'text/html');
        const images = blockContent.querySelectorAll('img');
        const svgs = blockContent.querySelectorAll('svg');
        return images.length > 0 || svgs.length > 0;
    }

    messageCenter(actionResult: ResourceAction): void {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as WireModel;
        const replacement = {} as LocalizationReplacementsModel;
        replacement.name = resource?.name;
        let errorIsHandled = true;

        let severity = messageSeverityEnum.success;
        let summary = `Amplify.wire.actions.growlMessage.${actionType}.heading`;
        let detail = `Amplify.wire.actions.growlMessage.${actionType}.message`;

        if (error) {
            severity = messageSeverityEnum.error;

            if (error.error === `A release named ${resource?.name} already exists`) {
                summary = `Amplify.wire.actions.growlMessage.error.${actionType}.heading`;
                detail = 'Amplify.wire.actions.errorMessageDuplicateName';
            } else if (error.status === HttpStatusCode.Locked) {
                // HttpStatusCode.Locked(423) is b/e code that indicates quota is not sufficient
                summary = 'Amplify.wire.error.growlMessage.insufficientQuota.summary';
                detail = 'Amplify.wire.error.growlMessage.insufficientQuota.detail';
                this.quotaWarningSubject.next(true);
            } else if (
                actionType === ResourceActionTypes.DELETE
                || actionType === ResourceActionTypes.COPY
                || (actionType === ResourceActionTypes.PUBLISH && error === 'noContent')
            ) {
                summary = `Amplify.wire.actions.growlMessage.error.${actionType}.heading`;
                detail = `Amplify.wire.actions.growlMessage.error.${actionType}.message`;
                if (actionResult.name) {
                    replacement.name = actionResult.name;
                }
                errorIsHandled = false;
            } else {
                summary = 'Amplify.wire.actions.errorHeading';
                detail = 'Amplify.wire.actions.errorMessageGeneric';
                errorIsHandled = false;
            }

            if (actionType === ResourceActionTypes.SUBMIT) {
                resource.status = ReleaseStatus.draft;
            }
        }

        this.messageService.add({
            severity,
            summary: this.localizationService.transform(summary),
            detail: this.localizationService.transform(detail, replacement)
        });

        if (!errorIsHandled) {
            throw error;
        }
    }

    isEditorialReview(): boolean {
        return this.isEditor;
    }

    renameWire(id: number, name: string) {
        this.isNameDuplicated$(name, id).pipe(
            mergeMap((duplicate) => {
                if (duplicate) {
                    throw new Error(CommonApiErrorEnum.duplicateName);
                } else {
                    return this.getById(id);
                }
            }),
            mergeMap((wire) => {
                if (!!wire) {
                    wire.name = name;
                    return this.updateWire(wire);
                }
            })
        ).subscribe({
            next: () => {
                this.refreshAllSubject.next();
            },
            error:  (err) => {
                if (err?.message === CommonApiErrorEnum.duplicateName) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Amplify.wire.actions.renameWire.error.summary',
                        detail: 'Amplify.wire.actions.errorMessageDuplicateName'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Amplify.wire.actions.renameWire.error.summary',
                        detail: 'Amplify.wire.actions.renameWire.error.detail'
                    });
                }
                this.refreshAllSubject.next();
            }
        });
    }

    openRenameWireDialog(wire: WireListItemModel) {
        const io: TextInputDialogModel = {
            inputs: {
                id: 'renameWire',
                heading: 'Amplify.dialogs.renameWire.heading',
                description: 'Amplify.dialogs.renameWire.description',
                placeholder: 'Amplify.dialogs.renameWire.placeholder',
                initialValue: wire.name,
                okLabel: 'Lhp.actions.rename',
                formValidator: (name: string) => {
                    return [];
                },
                submit: (name: string) => {
                    if (name !== wire.name) {
                        this.renameWire(wire.id, name);
                    }
                    return of(null);
                }
            }
        };

        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    updateVisibility(id: number, action: ActionsEnum){
        this.updateVisibilitySubject.next({
            id: id,
            isHidden: action === ActionsEnum.hide
        });
    }

    bulkUpdateWireVisibility(ids: number[], action: ActionsEnum) {
        const hidden = action === ActionsEnum.hide;
        const observables = ids.map(id => {
            return this.wireApi.getById(id).pipe(
                switchMap(wire => {
                    wire.hidden = hidden;
                    return this.wireApi.update(wire);
                })
            );
        });
        forkJoin(observables).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Amplify.dialogs.bulkUpdateWire.success.summary',
                    detail: this.localizationService.transform('Amplify.dialogs.bulkUpdateWire.success.detail', {number: result.length})
                });
                // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                this.refreshAllSubject.next();
                this.refreshEditorialWireList.next();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Amplify.dialogs.bulkUpdateWire.error.summary',
                    detail: 'Amplify.dialogs.bulkUpdateWire.error.detail'
                });
            }
        });
    }

    /**
     * Adds provided newswires to a group (or removes group ID if Uncategorized is selected)
     * @param wireIds
     * @param group selected group
     * @param itemName if single item for growl message
     */
    public updateNewswireGroup(wireIds: number[], group: DisplayGroupModel, itemName?: string) {
        let request;
        if (group.id) {
            request = this.wireApi.updateNewswireDisplayGroup(wireIds, [], group.id);
        } else {
            request = this.wireApi.removeNewswiresFromDisplayGroup(wireIds);
        }
        request.subscribe({
            next: () => {
                this.refreshAllSubject.next();
                if (wireIds.length === 1) {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Common.lhp.moveToGroup.success.summary'),
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.single',
                            {itemName: itemName, groupName: group.name}
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Common.lhp.moveToGroup.success.summary'),
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.multiple',
                            {number: wireIds.length, name: group.name}
                        )
                    });
                }
            },
            error: (err) => {
                this.refreshAllSubject.next();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.moveToGroup.error.summary'),
                    detail: this.localizationService.transform('Common.lhp.moveToGroup.error.detail')
                });
            }
        });
    }

    /**
     * Removes group ID from all provided newswires
     * @param wireIds newswire IDs
     */
    public removeAllNewswiresFromGroup(wireIds: number[]) {
        this.wireApi.removeNewswiresFromDisplayGroup(wireIds).subscribe({
            next: () => {
                this.refreshAllSubject.next();
            }
        });
    }

    public updateNewswireFavorites(addIds: number[], removeIds: number[]) {
        this.wireApi.updateNewswireFavorites(addIds, removeIds).subscribe({
            next: () => {
                this.refreshAllSubject.next();
            },
            error: (err) => {
                this.refreshAllSubject.next();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.moveToGroup.error.summary'),
                    detail: this.localizationService.transform('Common.lhp.moveToGroup.error.detail')
                });
            }
        });
    }

    isWireNameDuplicated(): Observable<boolean> {
        return this.isWireNameDuplicated$;
    }
}
